import ApiClient from './apiClient';
import api from './api';
import { User } from './authService';
import type {
  DashboardOverviewResponse,
  UserAnalyticsResponse,
  OrderAnalyticsResponse,
  FinancialReportsResponse,
  GeographicAnalyticsResponse,
  RecentActivitiesResponse,
  PaginatedResponse,
  RecentOrder,
  TopUser
} from '../types/admin';

// Existing admin user management interfaces
export interface AdminUpdateUserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  is_admin?: boolean;
  use_for?: string;
  language?: string;
  company?: string;
  role?: string;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  house_no?: string;
  zip_code?: string;
}

export interface UserResponse {
  user: User;
  message: string;
}

// Admin service for both user management and dashboard APIs
const adminService = {
  // Existing user management functions
  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  getUser: async (id: number): Promise<User> => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  updateUser: async (id: number, data: AdminUpdateUserData): Promise<UserResponse> => {
    const response = await api.patch(`/admin/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  toggleAdmin: async (id: number): Promise<UserResponse> => {
    const response = await api.patch(`/admin/users/${id}/toggle-admin`);
    return response.data;
  },

  // Dashboard API functions using ApiClient
  getDashboardOverview: async (): Promise<DashboardOverviewResponse> => {
    return ApiClient.get<DashboardOverviewResponse>('/admin/dashboard/overview');
  },

  getUserAnalytics: async (timeRange = 30): Promise<UserAnalyticsResponse> => {
    return ApiClient.get<UserAnalyticsResponse>('/admin/dashboard/user-analytics', {
      time_range: timeRange
    });
  },

  getOrderAnalytics: async (timeRange = 30): Promise<OrderAnalyticsResponse> => {
    return ApiClient.get<OrderAnalyticsResponse>('/admin/dashboard/order-analytics', {
      time_range: timeRange
    });
  },

  getFinancialReports: async (timeRange = 30): Promise<FinancialReportsResponse> => {
    return ApiClient.get<FinancialReportsResponse>('/admin/dashboard/financial-reports', {
      time_range: timeRange
    });
  },

  getGeographicAnalytics: async (): Promise<GeographicAnalyticsResponse> => {
    return ApiClient.get<GeographicAnalyticsResponse>('/admin/dashboard/geographic-analytics');
  },

  getRecentActivities: async (): Promise<RecentActivitiesResponse> => {
    return ApiClient.get<RecentActivitiesResponse>('/admin/dashboard/recent-activities');
  },

  getRecentOrders: async (
    page = 1,
    perPage = 15
  ): Promise<PaginatedResponse<RecentOrder>> => {
    return ApiClient.get<PaginatedResponse<RecentOrder>>('/admin/dashboard/tables/recent-orders', {
      page,
      per_page: perPage
    });
  },

  getTopUsers: async (
    timeRange = 30,
    page = 1,
    perPage = 15
  ): Promise<PaginatedResponse<TopUser>> => {
    return ApiClient.get<PaginatedResponse<TopUser>>('/admin/dashboard/tables/top-users', {
      time_range: timeRange,
      page,
      per_page: perPage
    });
  },
};

// Named export for new dashboard functions
export const dashboardService = {
  getDashboardOverview: adminService.getDashboardOverview,
  getUserAnalytics: adminService.getUserAnalytics,
  getOrderAnalytics: adminService.getOrderAnalytics,
  getFinancialReports: adminService.getFinancialReports,
  getGeographicAnalytics: adminService.getGeographicAnalytics,
  getRecentActivities: adminService.getRecentActivities,
  getRecentOrders: adminService.getRecentOrders,
  getTopUsers: adminService.getTopUsers,
};

// Default export (maintaining backward compatibility)
export default adminService; 